// /spiral - full-page hypnotic spiral using Cleopatra's preferred canvas code.
// A gentle audio player lets listeners play her guided-audio sessions while
// the spiral spins for the full duration. Age-gate and site chrome are hidden
// so it's a true full-screen experience.
//
// Query params:
//   ?audio=my-pleasure   → autoloads My Pleasure
//   ?audio=remember-me   → autoloads Remember Me
//   ?direction=out       → outward spin (default is inward)
//   ?speed=0.04          → rotation speed (default 0.04)
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Play, Pause } from "lucide-react";

// Audio catalogue for the built-in player. Add more entries here anytime.
const AUDIO_TRACKS = {
  "remember-me": {
    title: "Remember Me",
    src: "https://customer-assets-lxgj4vgw.emergentagent.net/job_trance-bookings/artifacts/w28dc631_RememberMe.mp3",
  },
  // "my-pleasure": { title: "My Pleasure", src: "..." },
};

export default function Spiral() {
  const canvasRef = useRef(null);
  const audioRef = useRef(null);
  const rafRef = useRef(null);
  const [hideChrome, setHideChrome] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [params] = useSearchParams();
  const audioKey = params.get("audio");
  const initialDirection = params.get("direction") === "out" ? "out" : "in";
  const speedParam = parseFloat(params.get("speed"));
  const initialSpeed = Number.isFinite(speedParam) && speedParam > 0 ? speedParam : 0.04;
  const [direction, setDirection] = useState(initialDirection); // "in" | "out"
  const [speed, setSpeed] = useState(initialSpeed);
  const track = audioKey ? AUDIO_TRACKS[audioKey] : null;

  // Hide the site chrome while /spiral is open
  useEffect(() => {
    document.documentElement.classList.add("spiral-fullpage");
    return () => document.documentElement.classList.remove("spiral-fullpage");
  }, []);

  // Auto-hide overlay chrome after inactivity
  useEffect(() => {
    let timer;
    const reset = () => {
      setHideChrome(false);
      clearTimeout(timer);
      timer = setTimeout(() => setHideChrome(true), 5000);
    };
    reset();
    window.addEventListener("mousemove", reset);
    window.addEventListener("touchstart", reset);
    window.addEventListener("keydown", reset);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("mousemove", reset);
      window.removeEventListener("touchstart", reset);
      window.removeEventListener("keydown", reset);
    };
  }, []);

  // Canvas animation loop - Cleopatra's spiral code, sized to the viewport
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const resize = () => {
      const size = Math.max(window.innerWidth, window.innerHeight) * 1.1;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(size * dpr);
      canvas.height = Math.round(size * dpr);
      canvas.style.width = `${size}px`;
      canvas.style.height = `${size}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    let rotation = 0;

    const draw = () => {
      const w = canvas.width / (window.devicePixelRatio || 1);
      const h = canvas.height / (window.devicePixelRatio || 1);
      const cx = w / 2;
      const cy = h / 2;

      ctx.clearRect(0, 0, w, h);

      // Solid black background disc
      ctx.fillStyle = "#000000";
      ctx.beginPath();
      ctx.arc(cx, cy, Math.max(cx, cy), 0, Math.PI * 2);
      ctx.fill();

      // White spiral line
      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth = 14;
      ctx.lineCap = "round";
      ctx.beginPath();
      const maxRadius = Math.max(cx, cy) * 1.05;
      for (let i = 0; i < 1400; i++) {
        const angle = 0.1 * i + rotation;
        const radius = 0.6 * i;
        if (radius > maxRadius) break;
        const x = cx + radius * Math.cos(angle);
        const y = cy + radius * Math.sin(angle);
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();

      // Direction: "in" = viewer feels pulled inward
      rotation += direction === "in" ? -speed : speed;
      rafRef.current = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      window.removeEventListener("resize", resize);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [direction, speed]);

  const togglePlay = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      a.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    } else {
      a.pause();
      setPlaying(false);
    }
  };

  return (
    <div
      data-testid="page-spiral"
      className="fixed inset-0 bg-black flex items-center justify-center overflow-hidden z-[60]"
      style={{ margin: 0 }}
    >
      <canvas
        ref={canvasRef}
        data-testid="spiral-canvas"
        className="pointer-events-none"
        aria-hidden="true"
      />

      {/* Overlay chrome */}
      <div
        data-testid="spiral-chrome"
        className={`absolute top-0 left-0 right-0 flex items-center justify-between p-4 md:p-6 transition-opacity duration-1000 z-10 ${hideChrome ? "opacity-0 pointer-events-none" : "opacity-100"}`}
      >
        <Link
          to="/"
          data-testid="spiral-back"
          className="text-white/80 text-[11px] uppercase tracking-[0.22em] hover:text-white bg-black/40 backdrop-blur px-3 py-2"
        >
          ← Back
        </Link>
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid="spiral-direction"
            onClick={() => setDirection((d) => (d === "in" ? "out" : "in"))}
            className="text-white/80 text-[11px] uppercase tracking-[0.22em] hover:text-white bg-black/40 backdrop-blur px-3 py-2"
          >
            {direction === "in" ? "Inward" : "Outward"}
          </button>
          <button
            type="button"
            data-testid="spiral-slower"
            onClick={() => setSpeed((s) => Math.max(0.01, s - 0.01))}
            className="text-white/80 text-[11px] uppercase tracking-[0.22em] hover:text-white bg-black/40 backdrop-blur px-3 py-2"
          >
            Slower
          </button>
          <button
            type="button"
            data-testid="spiral-faster"
            onClick={() => setSpeed((s) => Math.min(0.15, s + 0.01))}
            className="text-white/80 text-[11px] uppercase tracking-[0.22em] hover:text-white bg-black/40 backdrop-blur px-3 py-2"
          >
            Faster
          </button>
        </div>
      </div>

      {/* Audio player (only if ?audio= query provided) */}
      {track && (
        <div
          data-testid="spiral-audio"
          className={`absolute bottom-0 left-0 right-0 flex flex-col items-center gap-3 pb-6 md:pb-10 transition-opacity duration-1000 z-10 ${hideChrome ? "opacity-0 pointer-events-none" : "opacity-100"}`}
        >
          <div className="flex items-center gap-4 bg-black/60 backdrop-blur border border-white/20 px-4 py-3 rounded-none">
            <button
              type="button"
              onClick={togglePlay}
              data-testid="spiral-audio-toggle"
              aria-label={playing ? "Pause" : "Play"}
              className="w-10 h-10 border border-white/40 flex items-center justify-center text-white hover:text-black hover:bg-white transition-colors"
            >
              {playing ? <Pause size={14} /> : <Play size={14} />}
            </button>
            <div className="text-white text-[11px] uppercase tracking-[0.22em]">{track.title}</div>
          </div>
          <audio
            ref={audioRef}
            src={track.src}
            data-testid="spiral-audio-el"
            onEnded={() => setPlaying(false)}
            preload="metadata"
          />
        </div>
      )}

      <style>{`
        html.spiral-fullpage body { overflow: hidden; }
        html.spiral-fullpage header,
        html.spiral-fullpage footer,
        html.spiral-fullpage [data-testid="age-gate-overlay"],
        html.spiral-fullpage [data-testid="sticky-mobile-cta"] { display: none !important; }
      `}</style>
    </div>
  );
}
